import React from 'react';
import PhoneInput from 'react-phone-input-2';
import { generatePath, Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { useFeature } from 'flagged';
import PropTypes from 'prop-types';
import ConfirmationPopup from '@/components/common/ConfirmationPopup';
import DeleteButton from '@/components/common/DeleteButton';
import EditButton from '@/components/common/EditButton';
import Loader from '@/components/common/Loader';
import ShowForViews from '@/components/common/ShowForViews';
import EditableRelations from '@/constants/constituents/EditableRelations';
import HouseholdRelationLabels from '@/constants/constituents/HouseholdRelationLabels';
import RelationNamesSingular from '@/constants/constituents/RelationNamesSingular';
import RelationStatus from '@/constants/constituents/RelationStatus';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import FeatureFlags from '@/constants/system/FeatureFlags';
import LoaderSizes from '@/constants/ui/LoaderSizes';
import stopPropagation from '@/constants/ui/stopPropagation';
import UserViewEnums from '@/constants/users/UserViewEnums';
import useConstituent from '@/lib/hooks/swr/useConstituent';
import translate from '@/lib/intl/translate';
import { DonorRelationType, DonorType } from '@/propTypes';
import {
  getPrimaryEmail,
  getPrimaryPhoneObject,
} from '@/utils/constituentUtils';
import { EditRelationDialog } from './RelationDialogs';

const translateRelationLabelAndStatus = (relation) => {
  const hasLabel = !!relation.relationLabel;

  let relationStatusTranslation;
  switch (relation.relationStatus) {
    case RelationStatus.FORMER:
      relationStatusTranslation = hasLabel
        ? 'RELATION_STATUS_FORMER_LABELED'
        : 'RELATION_STATUS_FORMER';
      break;
    case RelationStatus.DECEASED:
      relationStatusTranslation = hasLabel
        ? 'RELATION_STATUS_DECEASED_LABELED'
        : 'RELATION_STATUS_DECEASED';
      break;
    default:
      relationStatusTranslation = null;
      break;
  }

  let relationLabelTranslation;
  switch (relation.relationLabel) {
    case HouseholdRelationLabels.PARENT:
      relationLabelTranslation = 'RELATION_LABEL_PARENT';
      break;
    case HouseholdRelationLabels.CHILD:
      relationLabelTranslation = 'RELATION_LABEL_CHILD';
      break;
    case HouseholdRelationLabels.SPOUSE:
      relationLabelTranslation = 'RELATION_LABEL_SPOUSE';
      break;
    case HouseholdRelationLabels.SIBLING:
      relationLabelTranslation = 'RELATION_LABEL_SIBLING';
      break;
    case HouseholdRelationLabels.EXTENDED_FAMILY:
      relationLabelTranslation = 'RELATION_LABEL_EXTENDED_FAMILY';
      break;
    default:
      break;
  }

  if (relationStatusTranslation) {
    return translate(relationStatusTranslation, {
      label: relationLabelTranslation
        ? translate(relationLabelTranslation)
        : relation.relationLabel,
    });
  }

  return relationLabelTranslation
    ? translate(relationLabelTranslation)
    : relation.relationLabel;
};

const formatDate = (date) => format(parseISO(date), 'MM/yyyy');

const getDuration = ({ start, end }) => {
  if (!start && !end) {
    return null;
  }

  const startDate = start && formatDate(start);
  const endDate = end && formatDate(end);

  if (start && !end) {
    return translate('RELATION_RELATIONSHIP_DURATION_START_ONLY', {
      startDate,
    });
  }

  if (end && !start) {
    return translate('RELATION_RELATIONSHIP_DURATION_END_ONLY', {
      endDate,
    });
  }

  return translate('RELATION_RELATIONSHIP_DURATION', {
    startDate,
    endDate,
  });
};

function Entry({ value, children, className, ...props }) {
  if (!value) {
    return null;
  }

  return (
    <li className={className} {...props}>
      {children(value)}
    </li>
  );
}

Entry.propTypes = {
  value: PropTypes.any,
  className: PropTypes.string,
  children: PropTypes.func,
};

Entry.defaultProps = {
  value: undefined,
  className: undefined,
  children: (x) => x,
};

function ConstituentRelationCard({
  onClose,
  onNameHover,
  parent,
  relation,
  renderActions,
}) {
  const hasIsDeceased = useFeature(FeatureFlags.IS_DECEASED);
  const [isBusy, setIsBusy] = React.useState(false);

  const { data: constituent, removeRelationship } = useConstituent(
    relation.relatedId,
    {
      revalidateOnFocus: false,
    },
  );

  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);

  const openEditDialog = () => setIsEditDialogOpen(true);
  const closeEditDialog = () => setIsEditDialogOpen(false);

  const [isDeleteRelationDialogOpen, setIsDeleteRelationDialogOpen] =
    React.useState(false);

  const openDeleteRelationDialog = () => setIsDeleteRelationDialogOpen(true);
  const closeDeleteRelationDialog = () => setIsDeleteRelationDialogOpen(false);

  const confirmDeletePopupAnchorRef = React.useRef();

  const handleRemove = async () => {
    try {
      closeDeleteRelationDialog();
      setIsBusy(true);
      await removeRelationship(relation.relationshipId);
    } finally {
      setIsBusy(false);
    }
  };

  if (!constituent) {
    return null;
  }

  const canBeEdited = EditableRelations.includes(relation.relationName);

  const primaryEmail = getPrimaryEmail(constituent.emails);
  const primaryPhone = getPrimaryPhoneObject(constituent.phones);

  if (isBusy) {
    return (
      <div className="flex cursor-pointer items-center justify-center p-4 hover:bg-grey-100">
        <Loader isCentered size={LoaderSizes.LG} />
      </div>
    );
  }

  return (
    <>
      <EditRelationDialog
        parent={parent}
        relation={relation}
        open={isEditDialogOpen}
        onClose={closeEditDialog}
      />
      <div className="group flex cursor-pointer flex-col justify-start p-4 delay-200 duration-200 hover:bg-grey-100">
        <ConfirmationPopup
          anchorEl={confirmDeletePopupAnchorRef?.current}
          anchorOrigin={{ horizontal: 'left', vertical: 'center' }}
          confirmButtonLabel={translate('DELETE')}
          message={translate('RELATION_ACTION_REMOVE_CONFIRM', {
            relationName:
              RelationNamesSingular[relation.relationName].toLowerCase(),
          })}
          onClick={stopPropagation}
          onConfirm={handleRemove}
          onClose={closeDeleteRelationDialog}
          open={isDeleteRelationDialogOpen}
        />
        <div className="flex items-start justify-between">
          <ul className="grid grid-cols-1 gap-0.5">
            <Entry value={constituent.displayName} className="mb-1.5">
              {(value) => (
                <>
                  <Link
                    className="type-body2"
                    component={RouterLink}
                    to={generatePath(AuthRoutes.CONSTITUENT_INFO, {
                      constituentId: constituent.constituentId,
                    })}
                    onMouseEnter={onNameHover}
                    onClick={onClose}
                  >
                    {value}
                  </Link>
                  {hasIsDeceased && constituent.isDeceased && (
                    <span className="pl-1 font-normal text-text-secondary">
                      ({translate('DECEASED_LABEL')})
                    </span>
                  )}
                </>
              )}
            </Entry>
            <Entry value={translateRelationLabelAndStatus(relation)} />
            <Entry
              value={
                relation.relationStartDateString ||
                relation.relationEndDateString
              }
            >
              {() =>
                getDuration({
                  start: relation.relationStartDateString,
                  end: relation.relationEndDateString,
                })
              }
            </Entry>
            <Entry value={primaryEmail}>
              {(value) => <Link href={`mailto:${value}`}>{value}</Link>}
            </Entry>
            <Entry value={primaryPhone?.number}>
              {(value) => (
                <Link href={`tel:${value}`}>
                  <PhoneInput
                    disabled
                    inputClass="w-full border-0 p-0 cursor-pointer h-min"
                    buttonClass="hidden"
                    specialLabel=""
                    value={value}
                  />
                </Link>
              )}
            </Entry>
          </ul>
          <ShowForViews showFor={[UserViewEnums.ADMIN, UserViewEnums.EDITOR]}>
            <div className="flex flex-col items-center justify-end gap-2">
              <DeleteButton
                label={translate('RELATION_ACTION_REMOVE_LABEL', {
                  relationName: RelationNamesSingular[relation.relationName],
                })}
                onClick={openDeleteRelationDialog}
                ref={confirmDeletePopupAnchorRef}
                className="transition-opacity duration-200 hoverable:opacity-0 hoverable:group-hover:opacity-100"
                size="small"
              />
              {canBeEdited ? (
                <EditButton
                  label={translate('RELATION_ACTION_EDIT_LABEL')}
                  onClick={openEditDialog}
                  className="transition-opacity duration-200 hoverable:opacity-0 hoverable:group-hover:opacity-100"
                />
              ) : null}

              {renderActions?.(relation)}
            </div>
          </ShowForViews>
        </div>
      </div>
    </>
  );
}

ConstituentRelationCard.propTypes = {
  onClose: PropTypes.func,
  onNameHover: PropTypes.func,
  parent: DonorType.isRequired,
  relation: DonorRelationType.isRequired,
  renderActions: PropTypes.func,
};

ConstituentRelationCard.defaultProps = {
  onClose: () => {},
  onNameHover: () => {},
  renderActions: null,
};

export default ConstituentRelationCard;
